import { useId, useRef, useState } from "react";
import { initials, type Session } from "@/auth/session";
import { ChevronDownIcon } from "@/components/icons";
import AccountControls from "@/components/layout/AccountControls";
import { actionProps } from "@/lib/controlAnalytics";
import { TailorsPill } from "./SiteHeader";

/**
 * The avatar and its menu: the email, Support, the theme, the Overview (on a phone), the
 * envoy, the account, the plan, the guide and Sign out. On a phone the tailors pill is in it,
 * since the header keeps only the logo and the avatar there.
 */
export default function AccountMenu({ session }: { session: Session }) {
    const id = useId();
    const panel = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const name = [session.firstName, session.lastName]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(" ");
    return (
        <div>
            <button
                {...actionProps("account_menu_toggled", {
                    source: "AccountMenu",
                    next_state: !open,
                })}
                type="button"
                popoverTarget={id}
                aria-label={name ? `${name}, your account` : "Your account"}
                aria-expanded={open}
                aria-controls={id}
                className="press inline-flex h-10 max-[760px]:min-h-11 items-center gap-2 rounded-full border border-line-control bg-card pl-1 pr-2 text-fg transition-[background-color,transform] hover:bg-hover"
            >
                <span
                    className="grid size-8 place-items-center rounded-full bg-grad font-sans text-[12px] font-bold text-brand-fg"
                    aria-hidden="true"
                >
                    {initials(session)}
                </span>
                <ChevronDownIcon
                    className={`size-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
            </button>
            <div
                ref={panel}
                id={id}
                popover="auto"
                onToggle={(event) => setOpen(event.newState === "open")}
                aria-label="Account controls"
                className="menu-pop fixed inset-auto right-4 top-[68px] m-0 w-[min(260px,calc(100vw-32px))] rounded-card border border-line bg-card p-3 text-fg shadow-pop"
            >
                {session.email && (
                    <p
                        className="m-0 mb-2 truncate border-b border-line px-3.5 pb-2.5 pt-1 text-[14px] font-medium text-fg"
                        title={session.email}
                    >
                        {session.email}
                    </p>
                )}
                <div className="px-1 pb-2 min-[900px]:hidden">
                    <TailorsPill compact />
                </div>
                <AccountControls onNavigate={() => panel.current?.hidePopover()} />
            </div>
        </div>
    );
}
